import { useQuery } from '@tanstack/react-query';
import qs from 'query-string';
import { serialize } from 'routes/Explore/components/Sentence/helpers';
import {
    HttpContentWithData,
    HttpError,
    HttpResponse,
    RQAPICallWithData,
} from 'store/APIClient';
import { cleanQueryResult } from 'helpers/chartHelper';
import { isChartKey } from 'routes/Explore/components/Sentence/helpers/chartKey';
import { camelize } from 'helpers/syntaxHelper';
import { STALE_TIME } from 'business/constants';
import Semaphore from 'lib/semaphore';
import { useWidgetPositionContext } from 'lib/layout/context/WidgetPositionContext';
import { ChartDataQueryPayload } from '../ChartContainer';
import { ChartMaxValueQueryPayload } from './maxValue';
import { GetDataMakerResponse, GetLayoutChartDataResponse } from '../types';
import { getForceDefaultFilters } from 'components/QueryPerimeter/hooks/useGetDashboardFilters';

const NB_REQUEST_MAX_IN_PARALLEL = 5;

const throttler = new Semaphore(NB_REQUEST_MAX_IN_PARALLEL);

type QueryResultWrapper = {
    queryResult: {
        columns: ResponseColumnT[];
        data: Record<string, number>[];
    };
};

export const useGetDashboardChartData = (payload: ChartDataQueryPayload) => {
    const {
        chartKey,
        queryMetrics,
        queryDimensions,
        extraFilters,
        cacheKey,
        publicToken,
        enabled,
    } = payload;

    const args = {
        public_token: publicToken,
        filters: serialize.filters(extraFilters),
    };
    let endpoint = `explore/layouts/charts/dashboard/${chartKey}?${qs.stringify(
        args,
    )}`;
    if (getForceDefaultFilters()) {
        endpoint = `${endpoint}&force_default_filters=true`;
    }
    const widgetPosition = useWidgetPositionContext();
    const relativeYPosition = Math.abs(
        widgetPosition[widgetPosition.currentBreakpoint].y -
            widgetPosition.userInitialYPosition,
    );

    return useQuery<GetDataMakerResponse, HttpError>({
        queryKey: cacheKey,
        queryFn: async ({ signal }) => {
            const response = (await throttler.callFunction(
                async () =>
                    RQAPICallWithData<GetLayoutChartDataResponse>({
                        endpoint,
                        method: 'GET',
                        signal,
                    }),
                [relativeYPosition],
            )) as HttpResponse<HttpContentWithData<GetLayoutChartDataResponse>>;
            const { data } = response.content;
            const result = cleanQueryResult({
                queryMetrics,
                queryDimensions,
                columns: data?.queryResult?.columns ?? [],
                data: data?.queryResult?.data ?? [],
                timeFilters: data?.timeFilters ?? [],
                alertTriggered: data?.alertTriggered,
            });
            return result;
        },
        enabled: isChartKey(chartKey) && enabled,
        staleTime: STALE_TIME,
    });
};

export const useGetDashboardChartMaxValue = (
    payload: ChartMaxValueQueryPayload,
) => {
    const {
        chartKey,
        metrics,
        maxValueField,
        publicToken,
        disableDashboardFilters,
        dashboardFilters,
    } = payload;
    const metric: QueryMetricT | undefined =
        metrics && maxValueField
            ? {
                  field: maxValueField,
                  aggregation: metrics[0].aggregation,
              }
            : undefined;

    let endpoint = `explore/layouts/charts/dashboard/${chartKey}?is_gauge_max_value=True&public_token=${publicToken}`;

    if (getForceDefaultFilters()) {
        endpoint = `${endpoint}&force_default_filters=true`;
    }
    const queryKey = [
        'chart',
        chartKey,
        'maxValue',
        maxValueField,
        dashboardFilters,
        disableDashboardFilters,
    ];
    const widgetPosition = useWidgetPositionContext();
    const relativeYPosition = Math.abs(
        widgetPosition[widgetPosition.currentBreakpoint].y -
            widgetPosition.userInitialYPosition,
    );
    return useQuery<number | undefined, HttpError>({
        queryKey: queryKey,

        queryFn: async () => {
            const response = (await throttler.callFunction(
                async () => RQAPICallWithData<QueryResultWrapper>({ endpoint }),
                [relativeYPosition],
            )) as HttpResponse<HttpContentWithData<QueryResultWrapper>>;
            const { data } = response.content.data.queryResult;
            if (!data || data.length === 0) {
                console.warn(
                    `No data for runMaxValueQuery with chart ${chartKey}, skipping setDynamicMaxValue`,
                );
            }
            const result =
                data?.length > 0
                    ? data[0][camelize(maxValueField ?? '')]
                    : undefined;
            return result;
        },

        enabled: payload.shouldTriggerMaxValueQuery && Boolean(metric),
        staleTime: STALE_TIME,
    });
};
